import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "./icon";
import ConfirmDrawer from "./confirm-drawer";
import { NAV_ITEMS, type NavItem } from "./nav-items";
import { useProfileStore } from "@/store/profile-store";
import { useSettingStore } from "@/store/setting-store";
import { useLogout } from "@/features/auth/hooks/use-logout";
import { generateGradientFromId } from "@/utils/gradient";

/**
 * Pane width, expanded and collapsed. NavigationLayout pads the page content
 * by the matching amount — keep the two in sync.
 */
export const SIDEBAR_EXPANDED = "w-64";
export const SIDEBAR_COLLAPSED = "w-[68px]";

/**
 * The collapse animation, shared by the pane and by the page padding that
 * clears it. If the two ever disagree on duration or curve, the content drifts
 * away from the pane's edge mid-slide and the whole thing reads as a jump.
 */
export const SIDEBAR_TRANSITION = "duration-300 ease-panel";

/**
 * Rows are laid out at the expanded width in both states and let the pane clip
 * them, so collapsing never reflows their contents — only the pane's own width
 * animates. `w-64` minus the `px-2` the lists sit in.
 */
const ROW_WIDTH = "w-60";


const PRIMARY_ITEMS: NavItem[] = [
  ...NAV_ITEMS,
  {
    name: "wishlist",
    label: "علاقه‌مندی‌ها",
    href: "/profile/wishlist",
    icon: "heart",
    activeIcon: "heart-fill",
  },
];


const SECONDARY_ITEMS: NavItem[] = [
  {
    name: "settings",
    label: "تنظیمات",
    href: "/profile/settings",
    icon: "setting",
    activeIcon: "setting",
  },
];


const ROW_HEIGHT = 44;
const ROW_STRIDE = ROW_HEIGHT + 4;
const PILL_HEIGHT = 20;
const PILL_INSET = (ROW_HEIGHT - PILL_HEIGHT) / 2;


function SelectionIndicator({ index }: { index: number }) {
  return (
    <span
      aria-hidden
      style={{
        height: PILL_HEIGHT,
        transform: `translateY(${Math.max(index, 0) * ROW_STRIDE + PILL_INSET}px)`,
      }}
      className={`pointer-events-none absolute right-2 top-0 w-[3px] rounded-full bg-primary transition-[transform,opacity] duration-300 ease-out ${
        index < 0 ? "opacity-0" : "opacity-100"
      }`}
    />
  );
}


export default function DesktopSidebar() {
  const { pathname } = useLocation();
  const profile = useProfileStore((s) => s.profile);
  const tokens = useProfileStore((s) => s.tokens);
  const collapsed = useSettingStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useSettingStore((s) => s.toggleSidebar);

  const { logout, loggingOut, canLogout } = useLogout();
  const [logoutOpen, setLogoutOpen] = useState(false);

  const signedIn = Boolean(tokens?.access_token);
  const fullName = [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const initial = (profile?.first_name || "م").trim().charAt(0);


  // `pr-4` puts the icon 24px from the pane's edge, which is dead centre of the
  // 68px rail — so the icons don't shift when the label comes and goes.
  const rowClass = (isActive: boolean) =>
    `group relative flex h-11 shrink-0 ${ROW_WIDTH} items-center gap-3 rounded-xl pr-4 pl-3 text-sm font-medium transition-[background-color,color,transform] duration-200 ease-out active:scale-[0.97] ${
      isActive
        ? "bg-primary/10 text-primary"
        : "text-default-700 hover:bg-default-100"
    }`;

  const renderItem = (item: NavItem) => {
    const isActive = pathname === item.href;
    const label = item.longLabel ?? item.label;
    return (
      <Link
        key={item.name}
        to={item.href}
        title={collapsed ? label : undefined}
        aria-label={label}
        aria-current={isActive ? "page" : undefined}
        className={rowClass(isActive)}
      >
        <Icon
          name={isActive ? item.activeIcon : item.icon}
          size={20}
          className={`shrink-0 transition-colors duration-200 ${
            isActive ? "text-primary" : "text-default-500"
          }`}
        />
        {/* Kept mounted and faded rather than unmounted: dropping the text on
            the first frame pops the row before the pane has moved. */}
        <span
          className={`truncate transition-opacity duration-200 ease-out ${
            collapsed ? "opacity-0" : "opacity-100"
          }`}
        >
          {label}
        </span>
      </Link>
    );
  };

  const primaryIndex = PRIMARY_ITEMS.findIndex((i) => i.href === pathname);
  const secondaryIndex = SECONDARY_ITEMS.findIndex((i) => i.href === pathname);

  return (
    <>
      {/* Centred on the right edge at 80% height. Pinning top and bottom
          (inset-y) as well as a height would over-constrain the box — the
          browser drops `bottom` and the panel sticks to the top instead. */}
      <aside
        className={`fixed right-4 top-1/2 z-40 hidden h-[80vh] -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-default-100 bg-background transition-[width] lg:flex ${SIDEBAR_TRANSITION} ${
          collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED
        }`}
      >
        {/* `pr-4` centres the 36px button in the 68px rail, so it holds its
            place instead of sliding when the pane resizes. */}
        <div className="flex shrink-0 items-center p-2 pr-4">
          <button
            type="button"
            onClick={toggleSidebar}
            title={collapsed ? "باز کردن منو" : "جمع کردن منو"}
            aria-label={collapsed ? "باز کردن منو" : "جمع کردن منو"}
            aria-expanded={!collapsed}
            className="flex size-9 items-center justify-center rounded-lg text-default-500 transition-colors hover:bg-default-100"
          >
            {/* Expanding grows the panel leftwards, so the arrow points that way. */}
            <Icon name={collapsed ? "chevron-left" : "chevron-right"} size={18} />
          </button>
        </div>

        <Link
          to={signedIn ? "/profile" : "/login"}
          title={collapsed ? fullName || "حساب کاربری" : undefined}
          className={`mx-2 flex ${ROW_WIDTH} shrink-0 items-center gap-3 rounded-xl p-2 pr-1.5 transition-colors hover:bg-default-100`}
        >
          <div
            style={{ background: generateGradientFromId(Number(profile?.id) || 1) }}
            className="flex size-10 shrink-0 items-center justify-center rounded-full"
          >
            {profile?.avatar ? (
              <img
                src={profile.avatar}
                alt=""
                width={40}
                height={40}
                loading="lazy"
                decoding="async"
                className="size-full rounded-full object-cover"
              />
            ) : (
              <span className="text-base font-bold text-white">{initial}</span>
            )}
          </div>

          <div
            className={`min-w-0 transition-opacity duration-200 ease-out ${
              collapsed ? "opacity-0" : "opacity-100"
            }`}
          >
            <p className="truncate text-sm font-bold text-default-900">
              {fullName || (signedIn ? "کاربر آموزیم" : "مهمان")}
            </p>
            <p className="truncate text-xs text-default-500">
              {signedIn ? profile?.phone || "حساب کاربری" : "ورود به حساب"}
            </p>
          </div>
        </Link>

        {/* Rows are wider than the collapsed rail, so the horizontal axis has to
            clip rather than scroll. */}
        <nav className="relative mt-4 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden px-2">
          <SelectionIndicator index={primaryIndex} />
          {PRIMARY_ITEMS.map(renderItem)}
        </nav>

        <div className="border-t border-default-100 py-2">
          <nav className="relative flex flex-col gap-1 px-2">
            <SelectionIndicator index={secondaryIndex} />
            {SECONDARY_ITEMS.map(renderItem)}

            {canLogout && signedIn && (
              <button
                type="button"
                onClick={() => setLogoutOpen(true)}
                title={collapsed ? "خروج از حساب کاربری" : undefined}
                aria-label="خروج از حساب کاربری"
                className={`flex h-11 shrink-0 ${ROW_WIDTH} items-center gap-3 rounded-xl pr-4 pl-3 text-sm font-medium text-danger transition-[background-color,transform] duration-200 ease-out active:scale-[0.97] hover:bg-danger/10`}
              >
                <Icon name="logout" size={20} className="shrink-0 text-danger" />
                <span
                  className={`truncate transition-opacity duration-200 ease-out ${
                    collapsed ? "opacity-0" : "opacity-100"
                  }`}
                >
                  خروج از حساب کاربری
                </span>
              </button>
            )}
          </nav>
        </div>
      </aside>

      <ConfirmDrawer
        open={logoutOpen}
        onOpenChange={setLogoutOpen}
        variant="danger"
        title="خروج از حساب کاربری"
        description="برای دیدن دوره‌ها و اطلاعات حسابتان باید دوباره وارد شوید."
        confirmLabel="خروج"
        loading={loggingOut}
        onConfirm={async () => {
          await logout();
          setLogoutOpen(false);
        }}
      />
    </>
  );
}
